import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { apiClient } from '../services/apiClient';
import { Button } from './Button';

// Editor del system prompt que convierte una imagen en un estilo reutilizable
// (key `style_analysis_system`). Usa los endpoints genéricos de /mod
// (GET/PUT/DELETE /api/mod/prompts/:key) — no agrega rutas nuevas. Vive como
// componente aparte para no engordar ModPage.
interface PromptResponse {
  content: string;
  defaultContent: string;
  isOverridden: boolean;
  updatedAt: string | null;
}

const KEY = 'style_analysis_system';

const Wrap = styled.div`
  margin-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: 16px;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;
const Title = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;
const Hint = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
  line-height: 1.5;
`;
const Area = styled.textarea`
  width: 100%;
  min-height: 240px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  resize: vertical;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;
const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
  flex-wrap: wrap;
`;
const Status = styled.span<{ $err?: boolean }>`
  font-size: 12px;
  color: ${({ $err, theme }) => ($err ? theme.colors.error : theme.colors.textSecondary)};
`;
const Badge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 6px;
  background: #FEF3C7;
  color: #92400E;
`;

export function StyleAnalysisPromptEditor() {
  const [content, setContent] = useState('');
  const [saved, setSaved] = useState('');
  const [overridden, setOverridden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ text: string; err?: boolean } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<PromptResponse>(`/api/mod/prompts/${KEY}`);
      setContent(r.content);
      setSaved(r.content);
      setOverridden(r.isOverridden);
    } catch {
      setStatus({ text: 'Could not load the prompt', err: true });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const dirty = content !== saved;

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await apiClient.put(`/api/mod/prompts/${KEY}`, { content });
      setSaved(content);
      setOverridden(true);
      setStatus({ text: 'Saved. New styles will use it.' });
    } catch {
      setStatus({ text: 'Failed to save', err: true });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset to the default prompt? Your edited version will be deleted.')) return;
    setSaving(true);
    setStatus(null);
    try {
      await apiClient.delete(`/api/mod/prompts/${KEY}`);
      await load();
      setStatus({ text: 'Restored to default.' });
    } catch {
      setStatus({ text: 'Failed to restore', err: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrap>
      <TitleRow>
        <Title>Style from image (analysis)</Title>
        {overridden && <Badge>edited</Badge>}
      </TitleRow>
      <Hint>
        System prompt the AI uses to turn an uploaded image into a reusable style.
        Edit it to control how generic the styles come out (separating the visual treatment
        from the industry). Applies to styles created from now on.
      </Hint>
      <Area
        value={loading ? 'Loading…' : content}
        onChange={(e) => setContent(e.target.value)}
        disabled={loading || saving}
        spellCheck={false}
      />
      <Row>
        <Button $variant="secondary" $size="sm" onClick={save} disabled={!dirty || saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button $variant="outline" $size="sm" onClick={reset} disabled={saving || loading || !overridden}>
          Restore default
        </Button>
        {status && <Status $err={status.err}>{status.text}</Status>}
      </Row>
    </Wrap>
  );
}
